// AD-8 · Professional Passport · Public View — instructor settings (r2).
// Authenticated. Exposes the four AD-8 endpoints:
//   • visibility (opt-in / opt-out)
//   • display-name (set / clear)
//   • rotate-slug (privacy escape hatch)
// The public link is displayed only when the instructor has a slug
// (i.e. has ever opted in). All controls are calm, buttoned, and
// unambiguous per the operator-locked doctrine.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AD8_SETTINGS_LABELS, AD8_DISPLAY_NAME_MAX_LENGTH } from "../lib/ad8Copy";
import { useTruthState } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("access_token") || "";
  return { Authorization: `Bearer ${token}` };
}

export default function PassportProfessionalPublicSettings() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({
    is_visible: false,
    active_public_slug: null,
    display_name: null,
    display_name_updated_at: null,
  });
  const [nameDraft, setNameDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const truth = useTruthState();

  const load = useCallback(async () => {
    setLoading(true);
    // First-load pattern: hit the visibility endpoint with the
    // current value to force a state-row creation and refresh.
    try {
      const res = await axios.post(
        `${API}/passport/professional/public/visibility`,
        { is_visible: state.is_visible ?? false },
        { headers: authHeaders() },
      );
      setState(res.data);
      setNameDraft(res.data.display_name || "");
    } catch (err) {
      // Silent — settings page should still load calmly.
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  const toggleVisibility = useCallback(async (next) => {
    setBusy(true);
    try {
      await truth.run(
        async () => {
          const res = await axios.post(
            `${API}/passport/professional/public/visibility`,
            { is_visible: next },
            { headers: authHeaders() },
          );
          setState(res.data);
        },
        {
          completedMessage: next ? "Public passport is now visible." : "Public passport is now hidden.",
          failedMessage:    "Could not update visibility.",
        },
      );
    } catch { /* truth.run captured */ }
    setBusy(false);
  }, [truth]);

  const saveDisplayName = useCallback(async () => {
    setBusy(true);
    try {
      await truth.run(
        async () => {
          const res = await axios.post(
            `${API}/passport/professional/public/display-name`,
            { display_name: nameDraft || null },
            { headers: authHeaders() },
          );
          setState(res.data);
        },
        {
          completedMessage: "Display name saved.",
          failedMessage:    "Could not save display name.",
        },
      );
    } catch { /* truth.run captured */ }
    setBusy(false);
  }, [nameDraft, truth]);

  const clearDisplayName = useCallback(async () => {
    setNameDraft("");
    setBusy(true);
    try {
      await truth.run(
        async () => {
          const res = await axios.post(
            `${API}/passport/professional/public/display-name`,
            { display_name: null },
            { headers: authHeaders() },
          );
          setState(res.data);
        },
        {
          completedMessage: "Display name cleared.",
          failedMessage:    "Could not clear display name.",
        },
      );
    } catch { /* truth.run captured */ }
    setBusy(false);
  }, [truth]);

  const rotateSlug = useCallback(async () => {
    setBusy(true);
    try {
      await truth.run(
        async () => {
          const res = await axios.post(
            `${API}/passport/professional/public/rotate-slug`,
            {},
            { headers: authHeaders() },
          );
          setState((prev) => ({ ...prev, ...res.data }));
        },
        {
          completedMessage: "Public link rotated. The previous link no longer resolves.",
          failedMessage:    "Could not rotate the public link.",
        },
      );
    } catch { /* truth.run captured */ }
    setBusy(false);
  }, [truth]);

  if (loading) {
    return <div data-testid="ad8-settings-loading" className="min-h-screen bg-slate-950" />;
  }

  const publicUrl = state.active_public_slug
    ? `${window.location.origin}/passport/${state.active_public_slug}`
    : null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="ad8-settings-page">
      <div className="max-w-xl mx-auto px-4 py-8 space-y-8">
        <header>
          <h1 className="text-2xl font-semibold">Public Passport controls</h1>
          <p className="text-sm text-slate-400 pt-2">
            Your public passport is opt-in and controlled by you. It shows current
            professional practice statements based on your recent evidence — no
            metrics, no ratings, no learner information.
          </p>
        </header>

        <TruthReceipt state={truth.state} message={truth.message} onRetry={() => toggleVisibility(state.is_visible)} onDismiss={() => truth.setState(null)} />

        <section
          data-testid="ad8-visibility-block"
          className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-3"
        >
          <p className="text-sm font-medium text-slate-300">
            Visibility: {state.is_visible ? "Visible" : "Hidden"}
          </p>
          {state.is_visible ? (
            <button
              type="button"
              data-testid="ad8-visibility-off-btn"
              onClick={() => toggleVisibility(false)}
              disabled={busy}
              className="w-full py-3 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-60"
            >
              {AD8_SETTINGS_LABELS.visibilityOff}
            </button>
          ) : (
            <button
              type="button"
              data-testid="ad8-visibility-on-btn"
              onClick={() => toggleVisibility(true)}
              disabled={busy}
              className="w-full py-3 rounded-xl bg-emerald-700 hover:bg-emerald-600 disabled:opacity-60"
            >
              {AD8_SETTINGS_LABELS.visibilityOn}
            </button>
          )}
        </section>

        {publicUrl && (
          <section
            data-testid="ad8-public-url-block"
            className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-3"
          >
            <p className="text-xs uppercase tracking-widest text-slate-400">
              {AD8_SETTINGS_LABELS.publicUrlHeader}
            </p>
            <p
              data-testid="ad8-public-url-value"
              className="text-sm break-all text-slate-200 font-mono"
            >
              {publicUrl}
            </p>
            <p className="text-xs text-slate-500">
              If this link has been shared somewhere you no longer control, rotate it.
              Rotation immediately invalidates the old link.
            </p>
            <button
              type="button"
              data-testid="ad8-rotate-slug-btn"
              onClick={rotateSlug}
              disabled={busy}
              className="w-full py-3 rounded-xl bg-amber-700 hover:bg-amber-600 disabled:opacity-60"
            >
              {AD8_SETTINGS_LABELS.rotateSlug}
            </button>
          </section>
        )}

        <section
          data-testid="ad8-display-name-block"
          className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-3"
        >
          <label htmlFor="ad8-display-name-input" className="text-xs uppercase tracking-widest text-slate-400 block">
            {AD8_SETTINGS_LABELS.displayNameLabel}
          </label>
          <input
            id="ad8-display-name-input"
            data-testid="ad8-display-name-input"
            type="text"
            maxLength={AD8_DISPLAY_NAME_MAX_LENGTH}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            placeholder="e.g. Sarah"
            className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-500"
          />
          <div className="flex gap-3">
            <button
              type="button"
              data-testid="ad8-display-name-save-btn"
              onClick={saveDisplayName}
              disabled={busy || !nameDraft.trim()}
              className="flex-1 py-3 rounded-xl bg-sky-700 hover:bg-sky-600 disabled:opacity-60"
            >
              {AD8_SETTINGS_LABELS.displayNameSave}
            </button>
            <button
              type="button"
              data-testid="ad8-display-name-clear-btn"
              onClick={clearDisplayName}
              disabled={busy}
              className="py-3 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-60"
            >
              {AD8_SETTINGS_LABELS.displayNameClear}
            </button>
          </div>
        </section>

        <footer className="text-xs text-slate-500 leading-relaxed pt-4 border-t border-slate-800">
          Your public passport shows statements only when supported by recent
          professional-practice evidence. Statements come and go with the evidence —
          nothing is permanent. Hidden by default. Nothing is generated by AI.
        </footer>
      </div>
    </div>
  );
}
